<template>
  <div id="mainCalendar">
    <!-- <v-progress-linear :indeterminate="true"></v-progress-linear> -->
    <div v-if="!showMobileFeedbackModal && !showSingleRoutePropertyView">
      <mobile-date-picker
        ref="mobileDatePicker"
        :prop-date="selectedDate"
        :calendar-events-array="eventsMapKeys"
        :class="{ 'route-selected': !allRoutesShowing }"
        @date-clicked="closeSelectedDate($event)"
      >
        <template v-slot:dateLabel="props">
          <v-layout fill-height align-center justify-center>
            <v-flex
              v-show="allRoutesShowing"
              @click.stop="navigatePreviousDate"
            >
              <v-icon large>
                navigate_before
              </v-icon>
            </v-flex>
            <v-flex shrink style="min-width: 280px">
              <!-- {{ selectedDate | dateWithDay }} -->
              <v-text-field
                dark
                color="white"
                :value="props.date"
                :disabled="!allRoutesShowing"
                prepend-icon="event"
                readonly
              />
            </v-flex>
            <v-flex
              v-show="allRoutesShowing"
              class="text-xs-right"
              @click.stop="navigateNextDate"
            >
              <v-icon large>
                navigate_next
              </v-icon>
            </v-flex>
          </v-layout>
        </template>
        <template v-slot:pickerButtons="props">
          <v-layout id="buttonGroupPicker">
            <v-flex>
              <v-btn
                color="grey"
                class="font-weight-bold"
                block
                flat
                large
                @click="setDateToToday"
              >
                Today
              </v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                color="grey"
                class="font-weight-bold"
                block
                flat
                large
                @click="closeDatePicker"
              >
                Close
              </v-btn>
            </v-flex>
          </v-layout>
        </template>
      </mobile-date-picker>
      <!-- cont and blocker should be used to prevent user from clicking on routes when the date picker is opened. -->
      <transition name="slide-left">
        <all-routes
          v-if="allRoutesShowing"
          id="allRoutes"
          :routes="eventsMap[selectedDate]"
          :selected-date="selectedDate"
          :message-no-routes="message"
          @click-to-expand="expandRoute"
        />
      </transition>
      <title-with-message
        v-if="emptyRouteData"
        title="No routes present"
        message="There are no routes associated for your account"
        :content-small="true"
      />

      <transition name="slide-right">
        <single-route
          v-if="!allRoutesShowing"
          id="singleRoute"
          :route="expandedRoute"
          @show-property="showDetailedProperty($event)"
          @select-home="openMobileFeedback($event)"
        />
      </transition>
    </div>
    <feedback-component
      v-if="showMobileFeedbackModal && selectedFeedback"
      class="mobile-feedback-component"
      :feedback-data="selectedFeedback"
      :property-data="selectedProperty"
      @on-feedback-updated="updateFeedback($event)"
    />
    <detail-property-view
      v-if="showSingleRoutePropertyView"
      class="mobile-calendar-property-view"
      :property-object-id="propertyView"
    />
  </div>
</template>

<script>
import FeedbackComponent from '../feedback/FeedbackComponent'
import moment from 'moment'
import { mapState, mapMutations, mapActions } from 'vuex'
import AllRoutes from './MobileAllRoutes.vue'
import SingleRoute from './MobileSingleRoute.vue'
import DetailPropertyView from '../../components/search/property_view/DetailPropertyView'
import MobileDatePicker from '../uiUtils/MobileDatePicker.vue'
import TitleWithMessage from '../uiUtils/TitleWithMessage.vue'
import { genericGetRequestToApi } from '../../utils/genericRequests'
import { RetrieveProfileById } from '../../utils/clientUrls'
import {
  groupArrayByKey,
  sortObjectArrayByKey,
  isEmpty
} from '../../utils/generalUtils'
import { isDeclinedRoute, isNotScheduledType } from '../../utils/routeUtils'

export default {
  components: {
    AllRoutes,
    SingleRoute,
    MobileDatePicker,
    DetailPropertyView,
    TitleWithMessage,
    FeedbackComponent
  },
  filters: {
    dateWithDay(val) {
      return val ? moment(val).format('dddd, MMMM Do YYYY') : ''
    }
  },
  data: () => ({
    todayDate: new Date().toISOString().substr(0, 10),
    selectedDate: new Date().toISOString().substr(0, 10),
    beforeToday: '',
    afterToday: '',
    propertyView: null,
    selectedProperty: null,
    selectedFeedback: null
  }),
  computed: {
    ...mapState('common', ['profileStatus']),
    ...mapState('mobile', ['allRoutesShowing']),
    ...mapState('agent', ['agentData']),
    ...mapState('client', ['clientData']),
    ...mapState('common', [
      'routeData',
      'routesLoaded',
      'expandedRoute',
      'showMobileFeedbackModal',
      'showSingleRoutePropertyView'
    ]),

    emptyRouteData() {
      return this.routesLoaded && isEmpty(this.routeData)
    },

    eventsMap() {
      const eventsArray = this.generateCalendarEventArray(this.routeData)
      return groupArrayByKey(eventsArray, 'date')
    },
    message() {
      return (
        'Last day with routes is <i>' +
        moment(this.beforeToday).format('MMMM Do YYYY') +
        '</i>.<br>Next day with routes is <i>' +
        moment(this.afterToday).format('MMMM Do YYYY') +
        '</i>.<br>Use the arrows to navigate.'
      )
    },
    eventsMapKeys() {
      return Object.keys(this.eventsMap)
    }
  },
  watch: {
    clientData(newValue, oldValue) {
      if (newValue) {
        this.getRouteData({ data: newValue, type: 'client' })
      }
    },
    eventsMap(newValue, oldValue) {
      // Check the eventsMap for the dates closest to today and store them.
      if (newValue) {
        let before, after
        const dateToday = new Date(this.todayDate)
        for (let key in newValue) {
          if (key === this.todayDate) {
            continue
          }
          const date = new Date(key)
          const diff = dateToday.getTime() - date.getTime()
          if (diff > 0) {
            // The date is before today.
            if (before !== undefined) {
              // Compare to current this.beforeToday -> save if closer
              const dateBefore = new Date(before)
              const currentDiff = dateToday.getTime() - dateBefore.getTime()
              if (Math.abs(diff) < Math.abs(currentDiff)) {
                before = key
              }
            } else {
              before = key
            }
          } else {
            // The date is after today.
            if (after !== undefined) {
              // Compare to current this.beforeToday -> save if closer
              const dateAfter = new Date(after)
              const currentDiff = dateToday.getTime() - dateAfter.getTime()
              if (Math.abs(diff) < Math.abs(currentDiff)) {
                after = key
              }
            } else {
              after = key
            }
          }
        }
        this.beforeToday = before
        this.afterToday = after
      }
    }
  },
  mounted() {
    this.generateRouteData()
  },
  destroyed() {
    this.setShowSingleRoutePropertyView(false)
    this.setShowMobileFeedbackModal(false)
    this.setShowingMobileBackButton(false)
    this.setAllRoutesShowing(true)
  },
  methods: {
    ...mapActions('common', ['getRouteData']),
    ...mapMutations('mobile', {
      setAllRoutesShowing: 'SET_ALL_ROUTES_SHOWING'
    }),
    ...mapMutations('common', {
      setExpandedRoute: 'SET_EXPANDED_ROUTE',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL',
      setShowSingleRoutePropertyView: 'SET_SHOW_SINGLE_ROUTE_PROPERTY_VIEW'
    }),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    async openMobileFeedback(data) {
      this.selectedProperty = data
      this.setShowMobileFeedbackModal(true)
      this.selectedFeedback = { ...data, _id: data.favoriteId }
    },
    showDetailedProperty(item) {
      this.setShowSingleRoutePropertyView(true)
      this.propertyView = item.propertyId
    },
    addressClickedHandler(data) {
      this.setShowSingleRoutePropertyView(true)
      this.propertyView = data.propertyId
    },
    updateFeedback(item) {
      this.selectedFeedback = item
      this.setShowMobileFeedbackModal(false)
      const expandedRoute = { ...this.expandedRoute }
      expandedRoute.routeStops = expandedRoute.routeStops.map(stop => {
        if (stop.propertyId === item.propertyId) {
          stop.feedback = item.feedback
        }
        return stop
      })
      this.setExpandedRoute(expandedRoute)
    },
    closeSelectedDate(data) {
      this.selectedDate = new Date().toISOString().substr(0, 10)
      this.selectedDate = data
      this.closeDatePicker()
    },
    expandRoute(id) {
      this.closeDatePicker()
      const date = this.eventsMap[this.selectedDate]
      this.routeObj = date.find(elem => elem._id === id)
      let expandedRoute = this.routeObj
      if (this.expandedRoute) {
        return genericGetRequestToApi(
          RetrieveProfileById(this.routeObj.clientId)
        ).then(response => {
          expandedRoute.routeStops = expandedRoute.routeStops.map(route => {
            var feedback = {}
            var favoriteId = ''
            response.data.homes.map(item => {
              if (item['propertyId'] === route['propertyId']) {
                feedback = item.feedback
                favoriteId = item._id
              }
            })
            route['clientData'] = response.data
            route['feedback'] = feedback
            route['favoriteId'] = favoriteId
            this.setAllRoutesShowing(false)
            this.isClicked = true
            return route
          })
          this.setExpandedRoute(expandedRoute)
          this.setShowingMobileBackButton(true)
        })
      } else {
        this.setExpandedRoute({})
        this.setAllRoutesShowing(false)
      }
    },
    generateCalendarEventArray(routeData) {
      let eventsArray = []
      routeData.map(route => {
        const routeTime = moment(route.startTime)
        if (this.displayRoute(route)) {
          let timeFrom = null
          let timeTo = null
          let timeDiff = null
          if (route.routeStops.length > 1) {
            const anotherTemp = sortObjectArrayByKey(
              route.routeStops,
              'arrivalTime'
            )
            timeFrom = moment(anotherTemp[0].arrivalTime)
            timeTo = moment(anotherTemp[anotherTemp.length - 1].arrivalTime)
            timeDiff = timeTo.diff(
              timeFrom < routeTime ? timeFrom : routeTime,
              'minutes'
            )
          } else if (route.routeStops.length > 0) {
            timeFrom = moment(route.routeStops[0].arrivalTime).format('HH:mm')
            timeDiff = '60'
          }
          eventsArray.push({
            ...route,
            date: routeTime.format('YYYY-MM-DD'),
            time: routeTime.format('HH:mm'),
            timeDiff,
            open: false
          })
        }
      })
      return eventsArray
    },

    displayRoute(route) {
      if (route.state !== 'scheduled') return false
      if (isDeclinedRoute(route) || isNotScheduledType(route)) return false
      return true
    },

    generateRouteData() {
      if (this.profileStatus === 'agent') {
        if (this.agentData) {
          this.getRouteData({ data: this.agentData, type: 'agent' })
        }
      } else if (this.profileStatus === 'client') {
        if (this.clientData) {
          this.getRouteData({ data: this.clientData, type: 'client' })
        }
      }
    },
    navigatePreviousDate() {
      this.selectedDate = moment(this.selectedDate, 'YYYY-MM-DD')
        .subtract(1, 'days')
        .format('YYYY-MM-DD')
      // if (this.selectedDate === this.todayDate) {
      //   // We already have this stored.
      //   if (this.beforeToday) {
      //     this.selectedDate = this.beforeToday
      //   }
      // } else {
      //   // find current selected in keys, use the one before it
      //   let before
      //   const selectedDate = new Date(this.selectedDate)
      //   for (let key in this.eventsMap) {
      //     const date = new Date(key)
      //     const diff = selectedDate.getTime() - date.getTime()
      //     if (diff > 0) {
      //       // The date is before selected.
      //       if (before !== undefined) {
      //         // Compare to current this.beforeToday -> save if closer
      //         const dateBefore = new Date(before)
      //         const currentDiff = selectedDate.getTime() - dateBefore.getTime()
      //         if (Math.abs(diff) < Math.abs(currentDiff)) {
      //           before = key
      //         }
      //       } else {
      //         before = key
      //       }
      //     } else {
      //       continue
      //     }
      //   }
      //   // if (before) this.selectedDate = before
      //   this.selectedDate = moment(this.selectedDate, 'YYYY-MM-DD')
      //     .subtract(1, 'days')
      //     .format('YYYY-MM-DD')
      // }
    },
    navigateNextDate() {
      this.selectedDate = moment(this.selectedDate, 'YYYY-MM-DD')
        .add(1, 'days')
        .format('YYYY-MM-DD')
      // if (this.selectedDate === this.todayDate) {
      //   // We already have this stored.
      //   if (this.afterToday) {
      //     this.selectedDate = this.afterToday
      //   }
      // } else {
      //   // find current selected in keys, use the one before it
      //   let after
      //   const selectedDate = new Date(this.selectedDate)

      // for (const key in this.eventsMap) {
      //   const date = new Date(key)
      //   const diff = selectedDate.getTime() - date.getTime()
      //   if (diff < 0) {
      //     // The date is before selected.
      //     if (after !== undefined) {
      //       // Compare to current this.beforeToday -> save if closer
      //       const dateAfter = new Date(after)
      //       const currentDiff = selectedDate.getTime() - dateAfter.getTime()
      //       if (Math.abs(diff) < Math.abs(currentDiff)) {
      //         after = key
      //       }
      //     } else {
      //       after = key
      //     }
      //   } else {
      //     continue
      //   }
      // }
      // if (after) this.selectedDate = after
    },
    setDateToToday() {
      this.selectedDate = new Date().toISOString().substr(0, 10)
      this.closeDatePicker()
    },
    closeDatePicker() {
      this.$refs.mobileDatePicker.datePanel = []
    }
  }
}
</script>

<style lang="sass">
.route-selected
  #mobileFieldSelectedDate
    background: white
    input
      color: $hwayzMediumGray !important

.mobile-calendar-property-view
  position: absolute
  left: 0
  top: 0
  z-index: 10
</style>

<style lang="sass" scoped>
#currentDate
  background-color: white
  font-size: 1.8rem
  color: black
  height: 7rem
.button-passive
  i
    color: transparent !important
.content-blocker
  background-color: red
  position: absolute
  z-index: 0
  top: 0
  left: 0
  height: 100%
  width: 100%
#allRoutes, #singleRoute
  position: absolute
.slide-right-enter-active, .slide-right-leave-active, .slide-left-enter-active, .slide-left-leave-active,.slide-down-enter-active, .slide-down-leave-active
  transition: all 0.4s
.slide-right-enter, .slide-right-leave-to
  transform: translate(100%, 0)
.slide-left-enter, .slide-left-leave-to
  transform: translate(-100%, 0)
.slide-down-enter, .slide-down-leave-to
  transform: translate(0, 100%)
</style>
